import type { NextPage } from 'next'
import React, { useContext, useState } from 'react'
import useSWR from 'swr'
import { createClient } from 'graphqurl'
import Link from 'next/link'
import moment from 'moment'
import styles from '../../styles/Profile.module.scss'
import {
	Row,
	Col,
	Image,
	Card,
	Button,
	Modal
	// Spinner
} from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPencilAlt, faMapMarkerAlt, faEnvelope, faPhoneVolume, faFile, faTimes, faChevronLeft, faChevronRight, faWifi, faPenToSquare, faCircleStop } from '@fortawesome/free-solid-svg-icons'
import Head from 'next/head'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import CardBodyComponent from '../../components/CardBodyComponent'
import UserContext from '../../UserContext'
import BroadcastSuccess from '../../components/BroadcastSuccess'
import { Document, Page, pdfjs } from 'react-pdf'
import { useRouter } from 'next/router'


const Profile: NextPage = () => {
	// const { t }: any = props
	pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`
	const { user } = useContext(UserContext)
	const [show, setShow] = useState(false)
	const [cvPages, setCvPages] = useState(1)
	const [cvCurrentPage, setCvCurrentPage] = useState(1)
	const [showModal, setShowModal] = useState(false)
	const [action, setAction] = useState('')
	const route = useRouter()
	const locale = route.locale?.toUpperCase()

	const { t } = useTranslation()

	const fetcher = async (graphQLQuery): Promise<any> => {
		const token = window.localStorage.getItem('token')
		const client = createClient({
			endpoint: process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT as string,
			headers: { authorization: token !== null ? `Bearer ${token}` : '' },
		})
		try {
			const res = await client.query({
				query: graphQLQuery,
				variables: { id: user.id }
			})

			return res.data.Applicant_by_pk
		} catch (err) {
			// console.error(err)
		}
	}

	const swrKey = `
  query ($id: Int!) {
    Applicant_by_pk(id: $id) {
      id
      name
      avatar
      cv
      cvName
      gender
      dateOfBirth
      age
      nationality
      isStartWorkNow
      contact
      email
      unemployedDuration
      broadcastStartDate
      broadcastEndDate
      # Enum_Nationality { country alpha2 }
      Enum_WorkPass { display workPass }
      MrtStation { id name }
			City {
				id
				name
			}
      Enum_EducationLevel { display educationLevel }
      Enum_UnemployedDuration { description}
      ApplicantExperiences(where: {
        isDeleted: {_eq: false}
        jobTitle: {_neq: ""}
      }) {
        id
        jobTitle
        companyName
        occupation
        startDate
        endDate
        description
      }
      ApplicantEducations(where: {
        isRemoved: {_eq: false}
      }) {
        id
        schoolName
        educationOther
        fieldOfStudy
        startYear endYear
        grade
        description
        Enum_EducationLevel { display}
      }
    }
  }
  `

	const { data, error } = useSWR(swrKey, fetcher, { revalidateOnFocus: false })

	if (error !== undefined) return <div>Failed to load</div>
	if (data === undefined) return <div>Loading...</div>

	const { ApplicantExperiences: experiences, ApplicantEducations: educations } = data

	const handleUpdate = (action) => {
		setAction(action)
		setShowModal(true)
	}

	const getAge = (): number => {
		const dob = data.dateOfBirth
		if (dob !== null) {
			const age = moment().diff(moment(dob, 'YYYYMMDD'), 'years')
			return age
		} else return data.age
	}

	const capitalize = (): string => {
		const lowercaseStr = data.gender.toLowerCase()
		return data.gender.charAt(0) + lowercaseStr.slice(1)
	}

	const getBroadcastDate = (): string => {
		const broadcastDate = data.broadcastEndDate
		const date = moment(broadcastDate).format('DD/MM/YYYY')
		return date
	}

	const getExpiredBroadcast = (): boolean => {
		const isExpired = moment().isAfter(data.broadcastEndDate)
		return isExpired
	}

	return (
		<>
			<Head>
				<title>My profile</title>
			</Head>
			<div style={{ textAlign: 'right' }} className='mb-1'>
				<Link href='/update-profile' passHref>
					<Button
						variant='primary'
						className='fw-bold text-decoration-none text-white mb-3'
					>
						<FontAwesomeIcon icon={faPenToSquare} width='14' style={{ marginRight: '10px' }} />
						{t('profile_new.update_profile')}
					</Button>
				</Link>
			</div>
			<Card className='mb-2'>
				<Card.Header
					// style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
					className={`card-headings ${styles.title}`}
				>
					{t('profile_new.about_me')}
				</Card.Header>
				<Card.Body>
					<Row style={{ padding: '1rem' }}>
						<Col xl={2} lg={3} md={4} xs={12} className='d-flex align-items-start justify-content-center mb-4 mt-2 mr-2'>
							{
								data?.avatar ?
									<Image roundedCircle className={styles.imageSet} src={data.avatar} alt='avatar' /> :
									<Image roundedCircle className={styles.imageEmpty} src='/img/profile-placeholder.png' alt='avatar' />
							}
						</Col>
						<Col xs={12}>
							<h5 className={`d-flex align-items-center ${styles.name}`} style={{ marginBottom: '8px' }}>
								{data.name}
							</h5>
							{(data.age !== null || data.dateOfBirth !== null) && <p style={{ marginBottom: '8px', textTransform: 'capitalize' }}>{`${getAge()} years old${data.gender !== null ? `, ${capitalize()}` : ''}`}</p>}
							{
								(data.MrtStation && data.MrtStation !== null && locale === 'SG') && (
									<p className='d-flex align-items-center' style={{ marginBottom: '8px' }}>
										<FontAwesomeIcon icon={faMapMarkerAlt} width='13' color='darkslategray' style={{ marginRight: '10px' }} />
										{data.MrtStation.name}
									</p>)
							}
							{
								(data.City && data.City !== null && locale === 'MY') && (
									<p className='d-flex align-items-center' style={{ marginBottom: '8px' }}>
										<FontAwesomeIcon icon={faMapMarkerAlt} width='13' color='darkslategray' style={{ marginRight: '10px' }} />
										{data.City.name}
									</p>)
							}
							{data.email !== null && (
								<p className='d-flex align-items-center' style={{ marginBottom: '8px' }}>
									<FontAwesomeIcon icon={faEnvelope} width='13' color='darkslategray' style={{ marginRight: '10px' }} />
									{data.email}
								</p>)}
							{data.contact !== null && (
								<p className='d-flex align-items-center' style={{ marginBottom: '8px' }}>
									<FontAwesomeIcon icon={faPhoneVolume} width='13' color='darkslategray' style={{ marginRight: '10px' }} />
									{data.contact}
								</p>)}
							{
								data.cv !== null ? (
									<p>
										<a
											target='_blank'
											onClick={() => setShow(true)}
											className={styles.cvName}
										>
											<FontAwesomeIcon icon={faFile} width='12' color='#FF6465' style={{ marginRight: '10px' }} />
											{data.cvName !== null || data.cvName !== '' ? data.cvName : 'Attached doc.'}
										</a>
									</p>
								) : (
									<div style={{ marginBottom: '20px' }}>
										<Link
											href={{
												pathname: '/update-profile',
												query: { uploadResume: true }
											}}
										>
											{t('profile_new.attach_now')}
										</Link>
									</div>
								)
							}
							{(data.broadcastEndDate !== null && getExpiredBroadcast() !== true) ? (
								<div>

									<Button
										className={`${styles.broadcastingBtn} mb-2`}
										variant='outline-primary'
										onClick={() => handleUpdate('stop-broadcast')}
									>
										<FontAwesomeIcon icon={faCircleStop} /> &nbsp;&nbsp;
										{t('profile_new.stop_broadcast')}
									</Button>
									<p className='d-flex align-items-center mb-0'>
										{t('profile_new.your_profile_visible')}{getBroadcastDate()}
									</p>
								</div>
							) : (
								<Button
									className='text-white'
									variant='primary'
									onClick={() => handleUpdate('broadcast')}
								>
									<FontAwesomeIcon icon={faWifi} /> &nbsp;&nbsp;
									{t('profile_new.broadcast')}
								</Button>
							)}
							<br />
							<Modal
								show={showModal}
								onHide={() => {
									window.location.reload()
								}}
								size='lg'
							>
								<Modal.Body>
									<BroadcastSuccess isLogin={false} action={action} counter={0} />
								</Modal.Body>
							</Modal>
						</Col>
					</Row>
				</Card.Body>
			</Card>

			{
				(data.isStartWorkNow !== null || data.Enum_UnemployedDuration !== null) && (
					<Card className='mb-2'>
						<Card.Header className={styles.title}>{t('profile_new.availability')}</Card.Header>
						<Card.Body>
							<Row >
								<Col>
									<ul>
										{data.isStartWorkNow === true && <li>{t('profile_new.can_start_immediately')}</li>}
										{data.isStartWorkNow === false && <li>{t('profile_new.not_active_applying')}</li>}
									</ul>
								</Col>
							</Row>
						</Card.Body>
					</Card>
				)
			}

			<Card className='mb-2'>
				<Card.Header className={styles.title}>{t('profile_new.work_experience')}</Card.Header>
				{experiences.length === 0 && (
					<Row>
						<p className={styles.description}>
							{t('profile_new.no_work_experience')}
							<br />
							{t('profile_new.please_update_work_experience')}
						</p>
					</Row>
				)}
				{experiences.length > 0 && (
					<CardBodyComponent
						experiences={experiences}
						showDeleteBtn={false}
					/>
				)}
			</Card>

			<Card className='mb-2'>
				<Card.Header className={styles.title}>{t('profile_new.education')}</Card.Header>
				{educations.length === 0 && (
					<Row>
						<p className={styles.description}>
							{t('profile_new.no_education_experience')}
							<br />
							{t('profile_new.please_update_education')}
						</p>
					</Row>
				)}
				{educations.length > 0 && (
					<CardBodyComponent
						educations={educations}
						showDeleteBtn={false}
					/>
				)}
			</Card>
			<Modal centered show={show} size="xl" onHide={() => setShow(false)}>
				<Modal.Body className='px-4'>
					<div className={styles.modalClose}>
						<FontAwesomeIcon icon={faTimes} onClick={() => setShow(false)} />
					</div>
					<div className={styles.modalBody}>
						<Row>
							<Col>
								<div>
									<Document
										file={data.cv}
										onLoadSuccess={({ numPages }) => { setCvPages(numPages) }}
										options={{
											cMapUrl: 'cmaps/',
											cMapPacked: true
										}}
										className={styles.PDFDocument}
									>
										<Page pageNumber={cvCurrentPage} className={styles.PDFPage} />
									</Document>
								</div>
								<div className='mt-4'>
									<Row>
										<Col xs={12} sm={3}>
											<p>{t('profile.page')}: {cvCurrentPage}</p>
										</Col>
										<Col className={styles.pages}>
											{(cvPages > 1) && (
												<div>
													<Button
														className={styles.buttonLeft}
														onClick={() => {
															if (cvCurrentPage !== 1) setCvCurrentPage(cvCurrentPage - 1)
														}}
													>
														<FontAwesomeIcon icon={faChevronLeft} className={styles.arrowButton} /> &nbsp; {t('pag.prev')}
													</Button>
													<Button className={styles.buttonCenter}>{cvCurrentPage} / {cvPages}</Button>
													<Button
														className={styles.buttonRight}
														onClick={() => {
															if (cvCurrentPage !== cvPages) setCvCurrentPage(cvCurrentPage + 1)
														}}
													>
														{t('pag.next')} &nbsp; <FontAwesomeIcon icon={faChevronRight} className={styles.arrowButton} />
													</Button>
												</div>
											)}
										</Col>
									</Row>
								</div>
							</Col>
						</Row>
					</div>
				</Modal.Body>
			</Modal>
		</>
	)
}

export async function getStaticProps({ locale }) {
	return {
		props: {
			...(await serverSideTranslations(locale, ['common']))
			// Will be passed to the page component as props
		}
	}
}

export default Profile